import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, Share, Plus, MoreVertical } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const InstallPWAPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed or dismissed
    const isInStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                          window.navigator.standalone === true;
    setIsStandalone(isInStandalone);
    
    const wasDismissed = localStorage.getItem('pwa-install-dismissed');
    if (wasDismissed) {
      const dismissedTime = parseInt(wasDismissed);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        setDismissed(true);
      }
    }

    // Detect platform
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    const isAndroidDevice = /android/.test(userAgent);
    
    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);

    // Listen for beforeinstallprompt (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!isInStandalone && !wasDismissed) {
        setTimeout(() => setShowPrompt(true), 3000); // Show after 3 seconds
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show iOS/Android prompt after delay if not installed
    if (!isInStandalone && !wasDismissed && (isIOSDevice || isAndroidDevice)) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (isStandalone || dismissed || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <Card className="bg-white shadow-2xl border-emerald-200 overflow-hidden">
        <CardContent className="p-4">
          <button 
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
          
          <div className="flex items-start gap-4">
            {/* App Icon */}
            <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
              <span className="text-2xl">🦉</span>
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-slate-800 text-lg">Install Zazu App</h3>
              <p className="text-sm text-slate-500 mb-3">
                Add Zazu to your home screen for the best experience!
              </p>

              {/* Platform-specific instructions */}
              {deferredPrompt ? (
                <Button 
                  onClick={handleInstall}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Install Now
                </Button>
              ) : isIOS ? (
                <div className="space-y-2">
                  <p className="text-xs text-slate-600 font-medium">To install on iPhone/iPad:</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <div className="flex items-center justify-center w-6 h-6 bg-slate-100 rounded">
                      <Share className="w-4 h-4 text-blue-500" />
                    </div>
                    <span>1. Tap the Share button below</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <div className="flex items-center justify-center w-6 h-6 bg-slate-100 rounded">
                      <Plus className="w-4 h-4 text-slate-600" />
                    </div>
                    <span>2. Scroll and tap "Add to Home Screen"</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <div className="flex items-center justify-center w-6 h-6 bg-slate-100 rounded">
                      <span className="text-xs font-bold text-blue-500">Add</span>
                    </div>
                    <span>3. Tap "Add" to confirm</span>
                  </div>
                </div>
              ) : isAndroid ? (
                <div className="space-y-2">
                  <p className="text-xs text-slate-600 font-medium">To install on Android:</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <div className="flex items-center justify-center w-6 h-6 bg-slate-100 rounded">
                      <MoreVertical className="w-4 h-4" />
                    </div>
                    <span>1. Tap the menu (⋮) in your browser</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <div className="flex items-center justify-center w-6 h-6 bg-slate-100 rounded">
                      <Smartphone className="w-4 h-4" />
                    </div>
                    <span>2. Tap "Add to Home screen" or "Install app"</span>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-500">
                  <p>Install this app on your device for quick access anytime!</p>
                </div>
              )}
            </div>
          </div>

          {/* Benefits */}
          <div className="flex gap-4 mt-4 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <span className="text-emerald-500">✓</span> Offline access
            </div>
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <span className="text-emerald-500">✓</span> Faster loading
            </div>
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <span className="text-emerald-500">✓</span> Home screen icon
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InstallPWAPrompt;
